<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import type {
  NovaPostOrderEmits,
  NovaPostOrderProps,
} from './NovaPostOrder.types'

const emits = defineEmits<NovaPostOrderEmits>()
const props = defineProps<NovaPostOrderProps>()
const container = ref<HTMLDivElement>()
const openOrders = ref(false)
const selectedOrderName = computed(
  () =>
    props.orders.find((item) => item.value === props.activeOrder)?.label || ''
)
const clickAbleOrders = computed(() =>
  props.orders.filter((order) => order.value !== props.activeOrder)
)

const handleOnClickOrderContainer = () => {
  openOrders.value = !openOrders.value
}

const handleOnClickOrder = (value: NovaPostOrderProps['activeOrder']) => {
  emits('onChangeActiveOrder', value)
  openOrders.value = false
}

onClickOutside(container, () => {
  openOrders.value = false
})
</script>

<template>
  <div
    ref="container"
    :class="['nova-post-order', { on: openOrders }]"
    @click="handleOnClickOrderContainer"
  >
    <div class="inner">
      <div class="label-wrap">
        <span class="label">{{ selectedOrderName }}</span>

        <NovaIcon
          :icon="{ type: 'outline', name: 'chev-compact-bottom' }"
          class="chev"
        />
      </div>

      <div class="orders-wrap">
        <div class="orders">
          <button
            v-for="item in clickAbleOrders"
            :key="item.value"
            type="button"
            class="order"
            @click.stop="() => handleOnClickOrder(item.value)"
          >
            <span class="label">{{ item.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.nova-post-order {
  position: relative;
  display: flex;
  height: auto;
  min-width: 126px;
  &.on {
    .inner {
      z-index: 20;
      box-shadow: 0 4px 20px hex-to-rgba($color-black, 0.08);

      .label-wrap .chev {
        transform: rotate(180deg);
      }
      .orders-wrap {
        opacity: 1;
        margin-top: 8px;
        transform: translateY(0);
        pointer-events: auto;
      }
    }
  }

  .inner {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-shrink: 0;
    background-color: $color-white;
    border-radius: 5px;
    box-shadow: 0 2px 4px 0 rgba(148, 148, 148, 0.05);
    cursor: pointer;

    .label-wrap {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding-right: 5px;

      .label {
        color: $color-black;
        font-size: 14px;
        font-weight: 500;
        line-height: 22px;
        letter-spacing: -0.408px;
        padding: 9px 12px;
      }

      .chev {
        color: $color-text-4;
        transition: 0.2s;
        transform-origin: center;

        &.on {
          transform: rotate(180deg);
        }
      }
    }

    .orders-wrap {
      position: absolute;
      left: 0;
      top: 100%;
      width: 100%;
      height: auto;
      opacity: 0;
      @include transition(all 0.2s ease-in-out);
      pointer-events: none;
      z-index: $z-index-drop-down;

      .orders {
        display: flex;
        flex-direction: column;
        height: auto;
        max-height: 260px;
        border: 1px solid $color-black-5p;
        border-radius: 5px;
        background-color: #ffffff;

        .order {
          padding: 6px 12px;
          text-align: left;
          width: 100%;
          white-space: nowrap;
          @media (hover: hover) {
            &:hover {
              background-color: $color-bg-1;
              .label {
                color: $color-black;
              }
            }
          }
          .label {
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
            letter-spacing: -0.408px;
          }
        }
      }
    }
  }
}
</style>
